import { useEffect, useState, useRef, useCallback } from "react";
import { PerformanceStats } from "@/types/v2/AppAnalytics";
import { useStore } from "@/stores/useStore";
import { measureFPS, measureMemory } from "@/lib/performance";
import {
  APP_ANALYTICS_CHECK_INTERVAL_MS,
  APP_ANALYTICS_DELAY_MINS,
  PERFORMANCE_INTERVAL_MS,
  PERFORMANCE_MEASUREMENT_DURATION_MS,
} from "@/lib/const";

export const useAnalytics = () => {
  const {
    performance,
    setPerformance,
    lastSentTimestamp,
    sendAnalytics,
    setLastSentTimestamp,
  } = useStore();

  // Track if we're currently measuring performance
  const [isCollecting, setIsCollecting] = useState(false);

  // Refs for intervals to avoid stale closures
  const performanceIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const analyticsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Collect performance data continuously
  const collectPerformanceData = useCallback(async () => {
    if (isCollecting) return; // Prevent overlapping measurements

    setIsCollecting(true);

    try {
      // Measure FPS and memory simultaneously (shorter duration for frequent updates)
      const [fpsStats, memoryStats] = await Promise.all([
        measureFPS(PERFORMANCE_MEASUREMENT_DURATION_MS), // 2 second measurements
        measureMemory(PERFORMANCE_MEASUREMENT_DURATION_MS),
      ]);

      const newPerformanceStats: PerformanceStats = {
        fps: fpsStats.average,
        memory: {
          used: Math.round(memoryStats.used / 1024 / 1024), // Convert to MB
          total: Math.round(memoryStats.total / 1024 / 1024), // Convert to MB
        },
      };

      setPerformance(newPerformanceStats);

      if (process.env.NODE_ENV === "development") {
        console.log("📊 Performance Data Updated:", newPerformanceStats);
      }
    } catch (error) {
      console.error("Error collecting performance data:", error);
    } finally {
      setIsCollecting(false);
    }
  }, [isCollecting, setPerformance]);

  // Check if it's time to send analytics
  const checkAndSendAnalytics = useCallback(() => {
    const now = Date.now();
    const timeSinceLastSend = now - lastSentTimestamp;
    const shouldSend =
      timeSinceLastSend >= APP_ANALYTICS_DELAY_MINS * 60 * 1000;

    if (shouldSend) {
      sendAnalytics();

      if (process.env.NODE_ENV === "development") {
        console.log("⏰ Sending analytics data...", {
          lastSent: new Date(lastSentTimestamp).toISOString(),
          timeSinceLastSend:
            Math.round(timeSinceLastSend / 1000 / 60) + " minutes",
          performance,
        });
      }
    } else {
      const remainingTime =
        APP_ANALYTICS_DELAY_MINS * 60 * 1000 - timeSinceLastSend;
      const remainingMinutes = Math.round(remainingTime / 1000 / 60);

      if (process.env.NODE_ENV === "development") {
        console.log(`⏳ Next analytics send in ${remainingMinutes} minutes`);
      }
    }
  }, [lastSentTimestamp, performance, sendAnalytics]);

  // Initialize performance collection on mount
  useEffect(() => {
    // Start collecting performance data every 300 seconds
    performanceIntervalRef.current = setInterval(
      collectPerformanceData,
      PERFORMANCE_INTERVAL_MS
    );

    // Initial collection
    collectPerformanceData();

    return () => {
      if (performanceIntervalRef.current) {
        clearInterval(performanceIntervalRef.current);
      }
    };
  }, []);

  // Set up analytics sending interval
  useEffect(() => {
    // Check every 5 minutes if we should send analytics
    analyticsIntervalRef.current = setInterval(
      checkAndSendAnalytics,
      APP_ANALYTICS_CHECK_INTERVAL_MS
    );

    // Initial check
    checkAndSendAnalytics();

    return () => {
      if (analyticsIntervalRef.current) {
        clearInterval(analyticsIntervalRef.current);
      }
    };
  }, []);

  // Return current performance data and collection status
  return {
    performance,
    isCollecting,
    lastSentTimestamp,
    // Manual trigger functions for testing
    collectNow: collectPerformanceData,
    sendNow: () => {
      sendAnalytics();
      setLastSentTimestamp?.(Date.now());
    },
  };
};
